// src/pages/bulk/BulkConfigManagement.js
// Configuración comida al granel - valor umbral y acumulación

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Button, Table, Form, Spinner, Alert, Modal, ProgressBar } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { getBulkConfigs, postBulkConfig, getBulkAccum } from '../../services/bulkApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import './BulkConfigManagement.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const isGranel = (item) => {
  const w = (item.weight_name || item.weight?.weight_name || '').toUpperCase();
  return w === 'GRANEL';
};

// --- Modal Nueva/Editar Configuración ---
const ConfigModal = ({ show, onHide, config, onSuccess, allItems, configuredIds }) => {
  const { user } = useAuth();
  const [idItem, setIdItem] = useState(config?.id_item ?? '');
  const [thresholdValue, setThresholdValue] = useState(config?.threshold_value ?? '');
  const [notes, setNotes] = useState(config?.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!config?.id_item;

  const availableItems = useMemo(() => {
    if (isEdit && config) {
      return [{ id_item: config.id_item, sku: config.sku, description: config.description, product: { name: config.product_name } }];
    }
    return (allItems || []).filter((i) => isGranel(i) && !configuredIds.includes(i.id_item));
  }, [allItems, configuredIds, isEdit, config]);

  useEffect(() => {
    if (show) {
      setIdItem(config?.id_item ?? '');
      setThresholdValue(config?.threshold_value ?? '');
      setNotes(config?.notes ?? '');
      setError('');
    }
  }, [show, config]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!user?.uid) {
      setError('Sesión no válida.');
      return;
    }
    const itemId = parseInt(idItem, 10);
    const threshold = parseFloat(thresholdValue);
    if (!itemId || isNaN(itemId)) {
      setError('Selecciona un ítem.');
      return;
    }
    if (!threshold || threshold <= 0) {
      setError('El valor umbral debe ser mayor que 0.');
      return;
    }
    setLoading(true);
    const payload = { firebase_UID: user.uid, id_item: itemId, threshold_value: threshold };
    if (notes?.trim()) payload.notes = notes.trim();
    const res = await postBulkConfig(payload);
    setLoading(false);
    if (res.ok && (res.status === 200 || res.status === 201 || res.code === 200 || res.code === 201)) {
      onSuccess?.();
      onHide();
    } else {
      setError(res.data?.message || 'Error al guardar.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="bulk-modal">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Editar configuración' : 'Nueva configuración'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Ítem granel <span className="text-danger">*</span></Form.Label>
            <Form.Select value={idItem} onChange={(e) => setIdItem(e.target.value)} required disabled={isEdit}>
              <option value="">Selecciona ítem</option>
              {availableItems.map((it) => (
                <option key={it.id_item} value={it.id_item}>
                  {it.sku || it.id_item} - {it.description || it.product?.name || it.product_name || '-'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Valor umbral ($) <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={thresholdValue}
              onChange={(e) => setThresholdValue(e.target.value)}
              required
              placeholder="Ej: 200000"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notas (opcional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Form.Group>
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={loading}>{loading ? 'Guardando…' : 'Guardar'}</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// --- Modal Detalle / Acumulado ---
const AccumModal = ({ show, onHide, row }) => {
  const [detail, setDetail] = useState(row);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !row?.id_item) return;
    setDetail(row);
    setError('');
    if (row.accum_value != null && row.threshold_value != null) return;
    setLoading(true);
    getBulkAccum(row.id_item).then((res) => {
      if (res.ok) setDetail((d) => ({ ...d, accum_value: res.data?.accum_value ?? d?.accum_value }));
      else setError(res.data?.message || 'No se pudo cargar.');
      setLoading(false);
    });
  }, [show, row?.id_item]);

  if (!row) return null;
  const threshold = parseFloat(row.threshold_value) || 0;
  const accum = parseFloat(detail?.accum_value) || 0;
  const pct = threshold > 0 ? Math.min(100, (accum / threshold) * 100) : 0;

  return (
    <Modal show={show} onHide={onHide} centered className="bulk-modal">
      <Modal.Header closeButton>
        <Modal.Title>Acumulado</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="bulk-loading"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && (
          <>
            <div className="bulk-detail-info">
              <div className="info-item"><span className="label">SKU</span><span>{row.sku || '-'}</span></div>
              <div className="info-item"><span className="label">Producto</span><span>{row.product_name || '-'}</span></div>
              <div className="info-item"><span className="label">Descripción</span><span>{row.description || '-'}</span></div>
              <div className="info-item"><span className="label">Valor umbral</span><strong>{formatterCOP.format(threshold)}</strong></div>
              <div className="info-item"><span className="label">Acumulado actual</span><strong>{formatterCOP.format(accum)}</strong></div>
              <div className="info-item"><span className="label">Última actualización</span><span>{detail?.accum_updated_at || row.accum_updated_at || '-'}</span></div>
            </div>
            <div className="bulk-progress-wrap">
              <div className="bulk-progress-label">Progreso {pct.toFixed(1)}%</div>
              <ProgressBar now={pct} variant={pct >= 100 ? 'success' : 'warning'} className="bulk-progress-bar" />
            </div>
            <p className="bulk-explanation mt-3">Se descontará 1 unidad del inventario cuando el acumulado alcance o supere el umbral.</p>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// --- Componente principal ---
const BulkConfigManagement = () => {
  const { user } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showAccumModal, setShowAccumModal] = useState(false);
  const [accumRow, setAccumRow] = useState(null);
  const [alertMsg, setAlertMsg] = useState({ type: '', text: '' });

  const configuredIds = useMemo(() => configs.map((c) => c.id_item), [configs]);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    const res = await getBulkConfigs();
    if (res.ok) setConfigs(Array.isArray(res.data) ? res.data : []);
    else setConfigs([]);
    setLoading(false);
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const r = await dotoniwoofApi.get('/item.php');
      const data = r.data;
      const list = data?.message ?? data?.data ?? (Array.isArray(data) ? data : []);
      setAllItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error cargando ítems:', e);
      setAllItems([]);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const handleNewConfig = () => {
    setEditingConfig(null);
    setShowConfigModal(true);
  };

  const handleEditConfig = (row) => {
    setEditingConfig(row);
    setShowConfigModal(true);
  };

  const handleAccum = (row) => {
    setAccumRow(row);
    setShowAccumModal(true);
  };

  const handleConfigSuccess = () => {
    loadConfigs();
    setAlertMsg({ type: 'success', text: 'Configuración guardada correctamente.' });
    setTimeout(() => setAlertMsg({}), 3000);
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Helmet><title>Configuración comida al granel - DotoniWoof</title></Helmet>
      <Container fluid className="bulk-page">
        <motion.div className="bulk-header" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div>
            <h1>
              <span className="header-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                </svg>
              </span>
              Configuración comida al granel
            </h1>
            <p className="bulk-subtitle">Define el valor umbral de cada ítem granel. Las ventas se acumulan hasta alcanzar ese valor y entonces se descuenta 1 unidad del inventario.</p>
          </div>
          <Button variant="primary" className="btn-new-config" onClick={handleNewConfig}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            Nueva configuración
          </Button>
        </motion.div>

        {alertMsg.text && (
          <Alert variant={alertMsg.type} dismissible onClose={() => setAlertMsg({})} className="mb-3">
            {alertMsg.text}
          </Alert>
        )}

        <Card className="bulk-card">
          <Card.Body>
            {loading ? (
              <div className="bulk-loading"><Spinner animation="border" /></div>
            ) : configs.length === 0 ? (
              <div className="bulk-empty">
                <p>No hay configuraciones de comida al granel.</p>
                <Button variant="primary" onClick={handleNewConfig}>Agregar primera configuración</Button>
              </div>
            ) : (
              <div className="bulk-table-wrap">
                <Table responsive striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Producto</th>
                      <th>Descripción</th>
                      <th>Valor umbral</th>
                      <th>Acumulado actual</th>
                      <th>% progreso</th>
                      <th>Última actualización</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((row) => {
                      const th = parseFloat(row.threshold_value) || 0;
                      const acc = parseFloat(row.accum_value) || 0;
                      const pct = th > 0 ? Math.min(100, (acc / th) * 100) : 0;
                      return (
                        <tr key={row.id_item}>
                          <td>{row.sku || '-'}</td>
                          <td>{row.product_name || '-'}</td>
                          <td>{row.description || '-'}</td>
                          <td>{formatterCOP.format(th)}</td>
                          <td>{formatterCOP.format(acc)}</td>
                          <td>
                            <div className="bulk-progress-cell">
                              <ProgressBar now={pct} variant={pct >= 100 ? 'success' : 'warning'} className="bulk-progress-bar-inline" />
                              <span>{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                          <td>{row.accum_updated_at || '-'}</td>
                          <td>
                            <div className="bulk-actions">
                              <Button variant="outline-primary" size="sm" onClick={() => handleEditConfig(row)}>Editar</Button>
                              <Button variant="outline-secondary" size="sm" onClick={() => handleAccum(row)}>Ver acumulado</Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <ConfigModal show={showConfigModal} onHide={() => setShowConfigModal(false)} config={editingConfig} onSuccess={handleConfigSuccess} allItems={allItems} configuredIds={configuredIds} />
        <AccumModal show={showAccumModal} onHide={() => setShowAccumModal(false)} row={accumRow} />
      </Container>
    </>
  );
};

export default BulkConfigManagement;
